
import { Button, Empty, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { convertTimeTotz } from "@comps/commonFunctions"
import { getTopNotificationsReq, viewNotificationReq } from "./requests";
import { makeRequest } from "@utils/helpers";
import { CheckOutlined, ClockCircleOutlined } from "@ant-design/icons";

var selectedNotification=null;

const NOTIFICATION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'requests', label: 'Requests' },
  { key: 'payroll', label: 'Payroll' },
];

const NOTIFICATION_ROUTE_ALIASES = {
  'app/payroll-management/payrolls/index': 'payrollmanagement/payrolls',
  'payroll-management/payrolls/index': 'payrollmanagement/payrolls',
  'payroll-management/payrolls': 'payrollmanagement/payrolls',
  'payroll-management/my-payslips': 'payrollmanagement/my-payslips',
  'payroll-management/company-payslips': 'payrollmanagement/company-payslips',
  'masters/general/accolades': 'masters/general',
  'masters/system/users': 'masters/system',
};

const normalizeNotificationUrl = (sref) => {
  let url = String(sref || '').trim();

  if (!url) {
    return '';
  }

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsedUrl = new URL(url);
      url = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (error) {
      return url;
    }
  }

  url = url
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/#$/, '');

  if (url.startsWith('app.')) {
    url = url.replace(/^app\./, '').replace(/_/g, '-').replace(/\./g, '/');
  } else {
    url = url.replace(/^app\//, '');
  }

  url = url
    .replace(/^payroll-management\//, 'payrollmanagement/')
    .replace(/\/index$/, '')
    .replace(/\/+$/, '');

  url = NOTIFICATION_ROUTE_ALIASES[url] || url;

  return url ? `/${url.replace(/^\/+/, '')}` : '';
};

const TopNotification=(props)=>{
  const [notificationFilters, setNotificationFilters] = useState('all')
  const [notifications, setNotifications] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1);

  const onSuccessNotifications = useCallback((response) => {
    if(response.notifications.data.length<1){
      setHasMore(false)
    }
    setNotifications((prevNotifications) => {
      const existingIds = new Set(prevNotifications.map((item) => item.id));
      const fresh = response.notifications.data.filter((item) => !existingIds.has(item.id));
      if (!fresh.length) {
        setHasMore(false);
      }
      return [...prevNotifications, ...fresh];
    })
  }, []);

  const onError = useCallback((res, err) => {

  }, []);

  const getNotifications = useCallback((param) => {
    if(param==='initial'){
      setNotifications([]);
    }
    const payload = {
      page: currentPage,
      filters: ''
    }
    makeRequest(setLoader, getTopNotificationsReq, payload, onSuccessNotifications, onError);
  }, [currentPage, onError, onSuccessNotifications]);

  useEffect(() => {
    getNotifications(currentPage === 1 ? 'initial' : undefined)
  }, [currentPage, getNotifications])
    //===================notification start=======================================
  
    const handleScroll = (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.target;
      // Fetch more notifications if the user has scrolled to the bottom
      if (hasMore && scrollTop + clientHeight >= scrollHeight - 4) {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    };
  const onView=(item)=>{

    selectedNotification=item
  viewNotification(item)
  }
  const viewNotification=(item)=>{
      makeRequest(setLoader, viewNotificationReq, item.id, onSuccessView, onError);
  }
  const onSuccessView=(res)=>
  {
    const url=normalizeNotificationUrl(selectedNotification.url)
    if(url){
      navigate(url)
    }
    props.onCompleted(null)
  }

  const onChangeFilter = (filter) => {
    setNotificationFilters(filter);
  };

  const onMarkAllRead = () => {
    const unreadItems = filteredNotifications.filter((item) => !item.is_opened);
    const unreadIds = new Set(unreadItems.map((item) => item.id));
    unreadItems.forEach((item) => {
      makeRequest(() => {}, viewNotificationReq, item.id, () => {}, onError);
    });
    setNotifications((items) => items.map((item) => (unreadIds.has(item.id) ? { ...item, is_opened: true } : item)));
    props.onCompleted?.(null);
  };

  const getNotificationType = (item) => {
    const text = `${item.title || ''} ${item.message || ''}`.toLowerCase();
    if (text.includes('payroll') || text.includes('payslip') || text.includes('salary')) return 'payroll';
    if (text.includes('request') || text.includes('approv') || text.includes('rejected')) return 'requests';
    return 'general';
  };

  const getInitials = (item) => {
    const text = String(item.title || item.message || 'N').trim();
    return text.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'N';
  };

  const filteredNotifications = useMemo(() => {
    if (notificationFilters === 'all') return notifications;
    if (notificationFilters === 'unread') return notifications.filter((item) => !item.is_opened);
    return notifications.filter((item) => getNotificationType(item) === notificationFilters);
  }, [notifications, notificationFilters]);

  useEffect(() => {
    // keep pulling pages while the active filter has too few matches to fill the list
    if (!loader && hasMore && notifications.length > 0 && filteredNotifications.length < 10) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  }, [loader, hasMore, notifications.length, filteredNotifications.length]);

  const groupedNotifications = useMemo(() => {
    return filteredNotifications.reduce((groups, item) => {
      const date = convertTimeTotz(item.created_at);
      const group = date.isSame(convertTimeTotz(new Date()), 'day') ? 'Today' : 'Earlier';
      return { ...groups, [group]: [...(groups[group] || []), item] };
    }, {});
  }, [filteredNotifications]);

  const renderNotificationItem = (item) => {
    const type = getNotificationType(item);
    const imageUrl = item.image_url && item.image_url !== 'images/arnold-avatar.jpg' ? item.image_url : '';

    return (
      <button
        type="button"
        key={ item.id }
        className={ `top-notification-item${item.is_opened ? '' : ' is-unread'} is-${type}` }
        onClick={ () => onView(item) }
      >
        <span className="top-notification-item__avatar">
          { imageUrl ? <img src={ imageUrl } alt="" /> : getInitials(item) }
        </span>
        <span className="top-notification-item__body">
          <span className="top-notification-item__title">{ item.title }</span>
          <span className="top-notification-item__message">{ item.message }</span>
          <span className="top-notification-item__meta">
            <ClockCircleOutlined />
            { convertTimeTotz(item.created_at).format('YYYY-MM-DD h:mm a') }
          </span>
        </span>
      </button>
    );
  };

     return ( 
        <Spin spinning={loader}>
        <div className="top-notification-panel">
          <div className="top-notification-header">
            <span className="top-notification-header__icon"><CheckOutlined /></span>
            <div>
              <div className="top-notification-eyebrow">Inbox</div>
              <div className="top-notification-title">Notifications</div>
            </div>
          </div>
          <div className="top-notification-tabs" role="tablist" aria-label="Notification filters">
            { NOTIFICATION_FILTERS.map((filter) => (
              <button
                type="button"
                key={ filter.key }
                role="tab"
                aria-selected={ notificationFilters === filter.key }
                className={ notificationFilters === filter.key ? 'is-active' : '' }
                onClick={ () => onChangeFilter(filter.key) }
              >
                { filter.label }
              </button>
            )) }
          </div>
          <div className="top-notification-meta">
            <span>{ filteredNotifications.length } loaded</span>
            <Button type="link" onClick={ onMarkAllRead } disabled={ !filteredNotifications.some((item) => !item.is_opened) }>
              Mark all read
            </Button>
          </div>
          <div className="top-notification-list" onScroll={ handleScroll }>
            { !filteredNotifications.length && !loader ? (
              <div className="top-notification-empty">
                <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No notifications" />
              </div>
            ) : null }
            { ['Today', 'Earlier'].map((group) => groupedNotifications[group]?.length ? (
              <section className="top-notification-group" key={ group }>
                <h3>{ group }</h3>
                { groupedNotifications[group].map(renderNotificationItem) }
              </section>
            ) : null) }
            { !hasMore && filteredNotifications.length > 0 && (
              <div className="top-notification-end">All notifications loaded.</div>
            ) }
          </div>
        </div>
          </Spin>
     )
}

export default TopNotification
